import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaPlus, FaTrash, FaToggleOn, FaToggleOff } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebIntegrations,
  getWhatsAppWebIntegrationFields,
  createWhatsAppWebIntegration,
  updateWhatsAppWebIntegration,
  deleteWhatsAppWebIntegration,
} from "../../../../services/api";

const PLATFORM_LABEL = {
  shopee: "Shopee",
  amazon: "Amazon",
  magalu: "Magalu",
  mercadolivre: "Mercado Livre",
  natura: "Natura",
  aliexpress: "AliExpress",
};

export default function IntegrationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);

  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState("shopee");
  const [label, setLabel] = useState("");
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, fieldsRes] = await Promise.all([
        listWhatsAppWebIntegrations(),
        getWhatsAppWebIntegrationFields(),
      ]);
      setItems(list?.data || []);
      setFields(fieldsRes?.data || {});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  function openNew() {
    setPlatform("shopee");
    setLabel("");
    setCreds({});
    setShow(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await createWhatsAppWebIntegration({
        platform,
        label: label.trim() || null,
        credentials: creds,
      });
      setShow(false);
      reload();
    } catch (err) {
      alert("Falha ao salvar (verifique se todos os campos foram preenchidos).");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(item) {
    await updateWhatsAppWebIntegration(item.id, { is_active: !item.is_active });
    reload();
  }

  async function handleDelete(item) {
    if (!window.confirm(`Excluir integração ${PLATFORM_LABEL[item.platform]}?`)) return;
    await deleteWhatsAppWebIntegration(item.id);
    reload();
  }

  const requiredFields = fields[platform] || [];

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="mb-1">🔌 Integrações de Afiliado</h2>
              <p className="text-muted mb-0">
                Configure credenciais Shopee, Amazon, Magalu etc. pra conversão
                automática de links no Multi Sender.
              </p>
            </div>
            <Button variant="primary" onClick={openNew}>
              <FaPlus /> Adicionar Integração
            </Button>
          </div>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : items.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                <p className="mb-2">Nenhuma integração configurada.</p>
                <small className="text-muted d-block mb-3">
                  Sem integrações, o Multi Sender usa Gemini scraping (sem
                  conversão de afiliado).
                </small>
                <Button variant="primary" onClick={openNew}>
                  <FaPlus /> Cadastrar Primeira
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {items.map((it) => (
                <Col md={6} lg={4} key={it.id} className="mb-3">
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between">
                        <h5 className="mb-1">
                          {PLATFORM_LABEL[it.platform] || it.platform}
                        </h5>
                        <Badge variant={it.is_active ? "success" : "secondary"}>
                          {it.is_active ? "Ativa" : "Inativa"}
                        </Badge>
                      </div>
                      {it.label && (
                        <small className="text-muted">{it.label}</small>
                      )}
                      <hr />
                      <small className="text-muted d-block mb-2">
                        Configurado: ✅ {Object.keys(it.fields || {}).length} campo(s)
                      </small>
                      <div className="d-flex" style={{ gap: 6 }}>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => toggleActive(it)}
                          title="Ativar/Desativar"
                        >
                          {it.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(it)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>

      {/* Modal Nova Integração */}
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Integração</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Tipo</Form.Label>
            <Form.Control
              as="select"
              value={platform}
              onChange={(e) => {
                setPlatform(e.target.value);
                setCreds({});
              }}
            >
              {Object.keys(fields).map((p) => (
                <option key={p} value={p}>
                  {PLATFORM_LABEL[p] || p}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          <Form.Group>
            <Form.Label>Nome da Integração / Label</Form.Label>
            <Form.Control
              placeholder="Ex: Conta Caçadores Principal"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Form.Group>

          {requiredFields.map((f) => (
            <Form.Group key={f}>
              <Form.Label>{f} *</Form.Label>
              <Form.Control
                type={f.toLowerCase().includes("secret") ? "password" : "text"}
                value={creds[f] || ""}
                onChange={(e) =>
                  setCreds({ ...creds, [f]: e.target.value })
                }
              />
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
